import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { base44 } from "@/api/base44Client";

const AnimatedElement = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setIsVisible(true), delay);
        observer.unobserve(el);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      } ${className}`}
    >
      {children}
    </div>
  );
};

function ProductCard({ product, index }) {
  return (
    <AnimatedElement delay={index * 100}>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3 }}
        className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      >
        <div className="relative aspect-[4/3] bg-secondary/20 overflow-hidden">
          <img
            src={product.image_urls?.[0] || product.image_url || "https://via.placeholder.com/400x300?text=No+Image"}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          {product.badge && product.badge !== "None" && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-bold px-2.5 py-1 rounded-full shadow">
              {product.badge}
            </span>
          )}
        </div>
        
        <div className="p-5">
          <h3 className="font-bold text-xl mb-2 text-foreground group-hover:text-primary transition-colors">
            {product.name}
          </h3>
          
          {(product.short_description || product.description) && (
            <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
              {product.short_description || product.description}
            </p>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-primary">
              ${product.price}
            </span>
            <Link
              to={`/product/${product.id}`}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-4 py-2 rounded-full hover:bg-primary/90 transition-colors text-sm"
            >
              View Details
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </AnimatedElement>
  );
}

function AddOnCard({ addon, index }) {
  return (
    <AnimatedElement delay={index * 100}>
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3 }}
        className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      >
        <div className="p-6 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-bold text-xl text-foreground group-hover:text-primary transition-colors">
              {addon.name}
            </h3>
          </div>
          {addon.description && (
            <p className="text-muted-foreground text-sm leading-relaxed">{addon.description}</p>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-2xl font-bold text-primary">+${addon.price}</span>
            <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">Add-On</span>
          </div>
        </div>
      </motion.div>
    </AnimatedElement>
  );
}

function CategorySection({ title, products, viewAllLink, color }) {
  const [visible, setVisible] = useState(9);

  return (
    <section className="py-16 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <AnimatedElement>
          <div className="flex items-center justify-between mb-10">
            <h2 className={`font-extrabold text-3xl sm:text-4xl ${color || 'text-foreground'}`}>
              {title}
            </h2>
            {products.length > 0 && (
              <Link
                to={viewAllLink}
                className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
              >
                View All
                <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
        </AnimatedElement>

        {products.length === 0 ? (
          <div className="text-center py-12 bg-secondary/20 rounded-2xl">
            <p className="text-muted-foreground">No products available in this category.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.slice(0, visible).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
            {visible < products.length && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setVisible((v) => v + 9)}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-8 py-3 rounded-full hover:bg-primary/90 transition-colors"
                >
                  Show More <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default function BrowseRentalsPage() {
  const [waterSlides, setWaterSlides] = useState([]);
  const [partyRentals, setPartyRentals] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      base44.entities.Product.filter({ category: "Water Slides", active_status: true }),
      base44.entities.Product.filter({ category: "Party Rentals", active_status: true }),
      base44.entities.AddOn.filter({ active_status: true }),
    ])
      .then(([slides, parties, extras]) => {
        setWaterSlides(slides);
        setPartyRentals(parties);
        setAddOns(extras);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <AnimatedElement>
            <h1 className="font-extrabold text-4xl sm:text-5xl md:text-6xl text-foreground mb-4">
              Browse All Rentals
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore our complete collection of water slides and party rentals for your next event.
            </p>
          </AnimatedElement>
        </div>
      </section>

      {/* Water Slides Section */}
      <CategorySection
        title="Water Slides"
        products={waterSlides}
        viewAllLink="/water-slides-rentals"
        color="text-primary"
      />

      {/* Party Rentals Section */}
      <CategorySection
        title="Party Rentals"
        products={partyRentals}
        viewAllLink="/party-rentals"
        color="text-accent"
      />

      {/* Add-Ons Section */}
      <section className="py-16 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          <AnimatedElement>
            <div className="flex items-center justify-between mb-10">
              <h2 className="font-extrabold text-3xl sm:text-4xl text-green-600">Add-Ons & Extras</h2>
              {addOns.length > 0 && (
                <Link to="/add-ons" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline">
                  View All <ArrowRight className="w-5 h-5" />
                </Link>
              )}
            </div>
          </AnimatedElement>
          {addOns.length === 0 ? (
            <div className="text-center py-12 bg-secondary/20 rounded-2xl">
              <p className="text-muted-foreground">No add-ons available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {addOns.slice(0, 6).map((addon, index) => (
                <AddOnCard key={addon.id} addon={addon} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}